import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TodoProvider } from './context/TodoContext';
import { SwatchProvider } from './context/SwatchContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { CookieConsentBanner } from './components/common/CookieConsentBanner';

const Login = lazy(() =>
  import('./pages/Login').then(m => ({ default: m.Login }))
);
const Register = lazy(() =>
  import('./pages/Register').then(m => ({ default: m.Register }))
);
const VerifyEmail = lazy(() =>
  import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail }))
);
const VerificationRequired = lazy(() =>
  import('./pages/VerificationRequired').then(m => ({
    default: m.VerificationRequired,
  }))
);
const ForgotPassword = lazy(() =>
  import('./pages/ForgotPassword').then(m => ({
    default: m.ForgotPassword,
  }))
);
const ResetPassword = lazy(() =>
  import('./pages/ResetPassword').then(m => ({
    default: m.ResetPassword,
  }))
);
const Dashboard = lazy(() =>
  import('./pages/Dashboard').then(m => ({ default: m.Dashboard }))
);
const PrivacyPolicy = lazy(() =>
  import('./pages/PrivacyPolicy').then(m => ({
    default: m.PrivacyPolicy,
  }))
);
const TermsOfService = lazy(() =>
  import('./pages/TermsOfService').then(m => ({
    default: m.TermsOfService,
  }))
);

function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <ThemeProvider>
        <TodoProvider>
          <SwatchProvider>{children}</SwatchProvider>
        </TodoProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProviders>
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/verify-required"
              element={<VerificationRequired />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <CookieConsentBanner />
      </AppProviders>
    </BrowserRouter>
  );
}
